import { Controller } from "@hotwired/stimulus"

// Markdown editor enhancements on top of a plain <textarea>:
//
//   - Format toolbar (B / I / H2 / H3 / list / quote / code / table / hr)
//   - Insert infobox skeleton from a dropdown driven by infobox_layouts
//   - Page + File pickers with debounced autocomplete (↑/↓/Enter/Esc),
//     thumbnails for files, and a "Create redlink" fallback for unknown pages
//   - Live server-side preview (POSTs the body to the renderer endpoint)
//   - ⌘/Ctrl + B / I / K shortcuts
//
// The persisted form is still plain Markdown.
export default class extends Controller {
  static targets = [
    "textarea", "preview", "previewPane",
    "infoboxSelect",
    "picker", "pickerTitle", "pickerInput", "pickerResults",
  ]
  static values = {
    infoboxesUrl: String,
    pagesUrl: String,
    mediaUrl: String,
    previewUrl: String,
    csrfToken: String,
  }

  connect() {
    this.loadInfoboxes()

    this.pickerKind = null
    this.pickerSelection = ""
    this.pickerResults = []
    this.pickerActiveIndex = -1
    this.searchAbort = null
    this.searchTimer = null
    this.savedSelection = null

    this.boundDocumentClick = this.handleOutsideClick.bind(this)
    document.addEventListener("mousedown", this.boundDocumentClick)
  }

  disconnect() {
    document.removeEventListener("mousedown", this.boundDocumentClick)
  }

  // =========================================================================
  // Text-manipulation primitives
  // =========================================================================

  get textarea() { return this.textareaTarget }

  surround(before, after = before) {
    const ta = this.textarea
    const start = ta.selectionStart
    const end = ta.selectionEnd
    const selected = ta.value.slice(start, end)
    const replaced = `${before}${selected}${after}`
    ta.setRangeText(replaced, start, end, "select")
    if (!selected) {
      ta.setSelectionRange(start + before.length, start + before.length)
    }
    ta.focus()
  }

  prefixLines(prefix) {
    const ta = this.textarea
    const value = ta.value
    let start = ta.selectionStart
    const end = ta.selectionEnd
    while (start > 0 && value[start - 1] !== "\n") start -= 1
    const block = value.slice(start, end || start + 1)
    const replaced = block.replace(/^(?!$)/gm, prefix)
    ta.setRangeText(replaced, start, end || start + 1, "end")
    ta.focus()
  }

  insertAtCursor(text, { caretOffsetFromStart = null, selectLength = 0 } = {}) {
    const ta = this.textarea
    const start = ta.selectionStart
    ta.setRangeText(text, start, ta.selectionEnd, "end")
    if (caretOffsetFromStart !== null) {
      ta.setSelectionRange(start + caretOffsetFromStart, start + caretOffsetFromStart + selectLength)
    }
    ta.focus()
  }

  // =========================================================================
  // Toolbar — simple formatters
  // =========================================================================

  bold(e)        { e.preventDefault(); this.surround("**") }
  italic(e)      { e.preventDefault(); this.surround("*") }
  strike(e)      { e.preventDefault(); this.surround("~~") }
  code(e)        { e.preventDefault(); this.surround("`") }
  h2(e)          { e.preventDefault(); this.prefixLines("## ") }
  h3(e)          { e.preventDefault(); this.prefixLines("### ") }
  list(e)        { e.preventDefault(); this.prefixLines("- ") }
  orderedList(e) { e.preventDefault(); this.prefixLines("1. ") }
  quote(e)       { e.preventDefault(); this.prefixLines("> ") }
  hr(e)          { e.preventDefault(); this.insertAtCursor("\n---\n") }

  table(e) {
    e.preventDefault()
    this.insertAtCursor(
      "\n| Cột 1 | Cột 2 |\n| --- | --- |\n| ô 1 | ô 2 |\n| ô 3 | ô 4 |\n"
    )
  }

  // =========================================================================
  // Infobox dropdown
  // =========================================================================

  async loadInfoboxes() {
    if (!this.hasInfoboxSelectTarget || !this.infoboxesUrlValue) return
    try {
      const res = await fetch(this.infoboxesUrlValue, { headers: { Accept: "application/json" } })
      if (!res.ok) return
      this.infoboxLayouts = await res.json()
      for (const layout of this.infoboxLayouts) {
        const opt = document.createElement("option")
        opt.value = layout.slug
        opt.textContent = `${layout.title} (${layout.slug})`
        this.infoboxSelectTarget.appendChild(opt)
      }
    } catch (_) {}
  }

  insertInfobox(e) {
    const slug = e.target.value
    if (!slug) return
    const layout = (this.infoboxLayouts || []).find((l) => l.slug === slug)
    if (!layout) return
    const fieldLines = layout.fields.map((f) => `  ${f.name}=`).join("\n  | ")
    const skeleton = layout.fields.length === 0
      ? `{{${slug}}}`
      : `{{${slug}|${fieldLines.replace(/^\s+\| /, "")}}}`
    this.insertAtCursor(`${skeleton}\n\n`)
    e.target.value = ""
  }

  // =========================================================================
  // Page / File picker with autocomplete
  // =========================================================================

  openPagePicker(e) { e.preventDefault(); this.openPickerFor("page") }
  openFilePicker(e) { e.preventDefault(); this.openPickerFor("file") }

  openPickerFor(kind) {
    if (!this.hasPickerTarget) return
    this.pickerKind = kind

    // Save the selection at the moment the picker opens so it survives the
    // focus shift into the picker input.
    const ta = this.textarea
    this.savedSelection = {
      start: ta.selectionStart,
      end: ta.selectionEnd,
      text: ta.value.slice(ta.selectionStart, ta.selectionEnd),
    }

    this.pickerTitleTarget.textContent =
      kind === "file" ? "Chèn tập tin" : "Chèn liên kết wiki"
    this.pickerInputTarget.placeholder =
      kind === "file" ? "Tìm theo tên tập tin…" : "Tìm theo tên trang…"

    this.pickerInputTarget.value = this.savedSelection.text
    this.pickerTarget.hidden = false
    this.pickerInputTarget.focus()
    this.pickerInputTarget.select()
    this.refreshPickerResults()
  }

  closePicker() {
    if (!this.hasPickerTarget) return
    this.pickerTarget.hidden = true
    this.pickerActiveIndex = -1
    this.pickerResults = []
    this.pickerResultsTarget.replaceChildren()
    if (this.searchAbort) this.searchAbort.abort()
    this.textarea.focus()
  }

  searchPicker() {
    if (this.searchTimer) clearTimeout(this.searchTimer)
    this.searchTimer = setTimeout(() => this.refreshPickerResults(), 180)
  }

  async refreshPickerResults() {
    const q = this.pickerInputTarget.value.trim()
    const url = this.pickerKind === "file" ? this.mediaUrlValue : this.pagesUrlValue
    if (!url) return

    if (this.searchAbort) this.searchAbort.abort()
    this.searchAbort = new AbortController()

    try {
      const res = await fetch(`${url}?q=${encodeURIComponent(q)}`, {
        headers: { Accept: "application/json" },
        signal: this.searchAbort.signal,
      })
      if (!res.ok) {
        this.renderPickerError(`HTTP ${res.status}`)
        return
      }
      this.pickerResults = await res.json()
      this.pickerActiveIndex = this.pickerResults.length > 0 ? 0 : -1
      this.renderPickerResults()
    } catch (err) {
      if (err.name === "AbortError") return
      this.renderPickerError(err.message)
    }
  }

  renderPickerError(message) {
    this.pickerResultsTarget.replaceChildren(this.emptyRow(`Lỗi: ${message}`))
  }

  emptyRow(message) {
    const li = document.createElement("li")
    li.className = "we-picker-row we-picker-row-empty"
    li.textContent = message
    return li
  }

  renderPickerResults() {
    this.pickerResultsTarget.replaceChildren()
    if (this.pickerResults.length === 0) {
      this.pickerResultsTarget.appendChild(this.emptyRow("Không có kết quả."))
      if (this.pickerKind === "page" && this.pickerInputTarget.value.trim()) {
        this.appendCreateRow()
      }
      return
    }
    this.pickerResults.forEach((item, idx) => {
      this.pickerResultsTarget.appendChild(this.buildRow(item, idx))
    })
  }

  appendCreateRow() {
    const li = document.createElement("li")
    li.className = "we-picker-row we-picker-create active"
    li.dataset.action = "mousedown->wiki-editor#choosePicker"
    li.dataset.index = "-1"
    const title = this.pickerInputTarget.value.trim()
    li.innerHTML = `<span class="we-picker-row-icon">＋</span>
      <span class="we-picker-row-title">Tạo liên kết đỏ tới "${this.escape(title)}"</span>`
    this.pickerResultsTarget.appendChild(li)
    this.pickerActiveIndex = -1
  }

  buildRow(item, idx) {
    const li = document.createElement("li")
    li.className = "we-picker-row"
    li.setAttribute("role", "option")
    li.dataset.action = "mousedown->wiki-editor#choosePicker"
    li.dataset.index = String(idx)
    if (idx === this.pickerActiveIndex) li.classList.add("active")

    if (this.pickerKind === "file") {
      const thumb = item.thumbnail_url
        ? `<img src="${this.escape(item.thumbnail_url)}" class="we-picker-row-thumb" alt="">`
        : `<span class="we-picker-row-icon">📄</span>`
      li.innerHTML = `${thumb}<span class="we-picker-row-title">${this.escape(item.filename)}</span>`
    } else {
      li.innerHTML = `<span class="we-picker-row-icon">📄</span>
        <span class="we-picker-row-title">${this.escape(item.title)}</span>
        <span class="we-picker-row-meta">/${this.escape(item.slug)}</span>`
    }
    return li
  }

  escape(str) {
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
  }

  pickerKey(event) {
    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.movePicker(1)
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this.movePicker(-1)
    } else if (event.key === "Enter") {
      event.preventDefault()
      this.commitPicker()
    } else if (event.key === "Escape") {
      event.preventDefault()
      this.closePicker()
    }
  }

  movePicker(delta) {
    const rows = this.pickerResultsTarget.querySelectorAll(".we-picker-row:not(.we-picker-row-empty)")
    const max = rows.length
    if (max === 0) return
    // Map "create redlink" row (index = -1) into the cycle as max.
    const createPresent = !!this.pickerResultsTarget.querySelector(".we-picker-create")
    const total = createPresent ? max + 1 : max
    const visibleIdx = this.pickerActiveIndex < 0 ? total - 1 : this.pickerActiveIndex
    const next = (visibleIdx + delta + total) % total
    this.pickerActiveIndex = (createPresent && next === total - 1) ? -1 : next

    rows.forEach((row) => row.classList.remove("active"))
    if (createPresent) {
      const create = this.pickerResultsTarget.querySelector(".we-picker-create")
      create.classList.toggle("active", this.pickerActiveIndex === -1)
    }
    if (this.pickerActiveIndex >= 0 && rows[this.pickerActiveIndex]) {
      rows[this.pickerActiveIndex].classList.add("active")
      rows[this.pickerActiveIndex].scrollIntoView({ block: "nearest" })
    }
  }

  choosePicker(event) {
    event.preventDefault()
    const idx = Number(event.currentTarget.dataset.index)
    this.insertPick(idx)
  }

  commitPicker() {
    this.insertPick(this.pickerActiveIndex)
  }

  insertPick(idx) {
    if (this.pickerKind === "page") {
      this.insertPagePick(idx)
    } else {
      this.insertFilePick(idx)
    }
    this.closePicker()
  }

  insertPagePick(idx) {
    let title
    if (idx === -1) {
      title = this.pickerInputTarget.value.trim()
    } else if (idx >= 0 && idx < this.pickerResults.length) {
      title = this.pickerResults[idx].title
    }
    if (!title) return

    // Restore the original cursor / selection so the link replaces what was
    // highlighted, even though focus has been bouncing around the picker.
    const ta = this.textarea
    const { start, end, text: selection } = this.savedSelection || { start: ta.selectionStart, end: ta.selectionEnd, text: "" }

    const insertion =
      selection && selection.trim() && selection.trim() !== title
        ? `[[${title}|${selection}]]`
        : `[[${title}]]`

    ta.setRangeText(insertion, start, end, "end")
    ta.focus()
  }

  insertFilePick(idx) {
    if (idx < 0 || idx >= this.pickerResults.length) return
    const filename = this.pickerResults[idx].filename
    if (!filename) return

    const ta = this.textarea
    const { start, end } = this.savedSelection || { start: ta.selectionStart, end: ta.selectionEnd }
    const before = `[[File:${filename}|right|320px|`
    const after = `]]`
    const insertion = `${before}${after}`
    ta.setRangeText(insertion, start, end, "end")
    // Drop the caret inside the empty caption slot so the user can type one.
    const caretAt = start + before.length
    ta.setSelectionRange(caretAt, caretAt)
    ta.focus()
  }

  // =========================================================================
  // Outside-click + preview + shortcuts
  // =========================================================================

  handleOutsideClick(event) {
    if (!this.hasPickerTarget || this.pickerTarget.hidden) return
    if (this.element.contains(event.target)) return
    this.closePicker()
  }

  async togglePreview(e) {
    e.preventDefault()
    if (!this.hasPreviewPaneTarget) return

    const showing = !this.previewPaneTarget.hidden
    if (showing) {
      this.previewPaneTarget.hidden = true
      return
    }

    this.previewTarget.textContent = "Đang dựng…"
    this.previewPaneTarget.hidden = false

    try {
      const res = await fetch(this.previewUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRF-Token": this.csrfTokenValue,
          Accept: "text/html",
        },
        body: new URLSearchParams({ body_md: this.textarea.value }),
      })
      const html = await res.text()
      this.previewTarget.innerHTML = html
    } catch (err) {
      this.previewTarget.textContent = `Lỗi: ${err.message}`
    }
  }

  shortcuts(event) {
    if (!(event.metaKey || event.ctrlKey)) return
    const key = event.key.toLowerCase()
    if (key === "b") { event.preventDefault(); this.surround("**") }
    else if (key === "i") { event.preventDefault(); this.surround("*") }
    else if (key === "k") { event.preventDefault(); this.openPickerFor("page") }
  }
}
